<template>
    <div class="container" v-if="editor">
        <div class="control-group">
            <div class="button-group">
                <button class="editor-button" @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                    :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }">
                    H1
                </button>
                <button class="editor-button" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                    :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }">
                    H2
                </button>
                <button class="editor-button" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                    :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }">
                    H3
                </button>
                <button class="editor-button" @click="editor.chain().focus().toggleBold().run()"
                    :disabled="!editor.can().chain().focus().toggleBold().run()"
                    :class="{ 'is-active': editor.isActive('bold') }">
                    Bold
                </button>
                <button class="editor-button" @click="editor.chain().focus().toggleItalic().run()"
                    :disabled="!editor.can().chain().focus().toggleItalic().run()"
                    :class="{ 'is-active': editor.isActive('italic') }">
                    Italic
                </button>
                <button class="editor-button" @click="editor.chain().focus().toggleStrike().run()"
                    :class="{ 'is-active': editor.isActive('strike') }">
                    Strike
                </button>
                <input type="color" @input="editor.chain().focus().setColor($event.target.value).run()"
                    :value="editor.getAttributes('textStyle').color">
                <button class="editor-button" @click="editor.chain().focus().toggleBulletList().run()"
                    :class="{ 'is-active': editor.isActive('bulletList') }">
                    Bullet list
                </button>
                <button class="editor-button" @click="editor.chain().focus().toggleOrderedList().run()"
                    :class="{ 'is-active': editor.isActive('orderedList') }">
                    Numbered list
                </button>
                <button class="editor-button" @click="editor.chain().focus().toggleBlockquote().run()"
                    :class="{ 'is-active': editor.isActive('blockquote') }">
                    Blockquote
                </button>
            </div>
            <div class="button-group">
                <button class="editor-button" @click="editor.chain().focus().setTextAlign('left').run()"
                    :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }">
                    Left
                </button>
                <button class="editor-button" @click="editor.chain().focus().setTextAlign('center').run()"
                    :class="{ 'is-active': editor.isActive({ textAlign: 'center' }) }">
                    Center
                </button>
                <button class="editor-button" @click="editor.chain().focus().setTextAlign('right').run()"
                    :class="{ 'is-active': editor.isActive({ textAlign: 'right' }) }">
                    Right
                </button>
                <button class="editor-button" @click="editor.chain().focus().setTextAlign('justify').run()"
                    :class="{ 'is-active': editor.isActive({ textAlign: 'justify' }) }">
                    Justify
                </button>
                <button class="editor-button" @click="editor.chain().focus().unsetTextAlign().run()">
                    Reset text align
                </button>
            </div>
            <div class="button-group">
                <button class="editor-button" @click="editor.chain().focus().setFontFamily('Inter').run()"
                    :class="{ 'is-active': editor.isActive('textStyle', { fontFamily: 'Inter' }) }">
                    Inter
                </button>
                <button class="editor-button" @click="editor.chain().focus().setFontFamily('serif').run()"
                    :class="{ 'is-active': editor.isActive('textStyle', { fontFamily: 'serif' }) }">
                    Serif
                </button>
                <button class="editor-button" @click="editor.chain().focus().setFontFamily('monospace').run()"
                    :class="{ 'is-active': editor.isActive('textStyle', { fontFamily: 'monospace' }) }">
                    Monospace
                </button>
                <button class="editor-button" @click="editor.chain().focus().setFontFamily('cursive').run()"
                    :class="{ 'is-active': editor.isActive('textStyle', { fontFamily: 'cursive' }) }">
                    Cursive
                </button>
                <button class="editor-button" @click="editor.chain().focus().unsetFontFamily().run()">
                    Reset font family
                </button>
            </div>
        </div>
        <editor-content :editor="editor" class="main-editor" />
        <div
            :class="{ 'character-count': true, 'character-count--warning': editor.storage.characterCount.characters() === charLimit }">
            <svg height="20" width="20" viewBox="0 0 20 20">
                <circle r="10" cx="10" cy="10" fill="#e9ecef" />
                <circle r="5" cx="10" cy="10" fill="transparent" stroke="currentColor" stroke-width="10"
                    :stroke-dasharray="`calc(${percentage} * 31.4 / 100) 31.4`"
                    transform="rotate(-90) translate(-20)" />
                <circle r="6" cx="10" cy="10" fill="white" />
            </svg>

            {{ editor.storage.characterCount.characters() }} / {{ charLimit }}
        </div>
    </div>
</template>

<script>
import { Color } from '@tiptap/extension-color'
import StarterKit from '@tiptap/starter-kit'
import { Editor, EditorContent } from '@tiptap/vue-3'
import CharacterCount from '@tiptap/extension-character-count'
import FontFamily from '@tiptap/extension-font-family'
import TextStyle from '@tiptap/extension-text-style'
import TextAlign from '@tiptap/extension-text-align'

export default {
    components: {
        EditorContent,
    },
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        charLimit: Number
    },
    emits: ['update:modelValue'],
    data() {
        return {
            editor: null,
        }
    },
    watch: {
        modelValue(value) {
            // HTML
            const isSame = this.editor.getHTML() === value

            // JSON
            // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

            if (isSame) {
                return
            }

            this.editor.commands.setContent(value, false)
        },
    },
    computed: {
        percentage() {
            return Math.round((100 / this.charLimit) * this.editor.storage.characterCount.characters())
        },
    },
    mounted() {
        this.editor = new Editor({
            extensions: [
                StarterKit,
                TextStyle,
                FontFamily,
                CharacterCount.configure({
                    limit: this.charLimit,
                }),
                Color.configure({
                    types: ['textStyle'],
                }),
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                }),
            ],
            content: this.modelValue,
            onUpdate: () => {
                // HTML
                this.$emit('update:modelValue', this.editor.getHTML())

                // JSON
                // this.$emit('update:modelValue', this.editor.getJSON())
            },
        })
    },
    beforeUnmount() {
        this.editor.destroy()
    },
}
</script>

<style lang="scss">
.main-editor {
    border-radius: 8px;
    border-width: 1px;
    border-style: solid;
    border-color: gray;
    padding: 10px;
    margin-top: 20px;
    margin-bottom: 20px;
}

.editor-button {
    margin: 4px;
    font-size: medium;
    color: #652d66;
    background: white;
    height: 30px;
    border-width: 1px;
    border-radius: 3px;
    border-color: gray;
}

.is-active {
    background: #652d66;
    color: white;
}

/* Basic editor styles */
.tiptap {
    :first-child {
        margin-top: 0;
    }

    /* List styles */
    ul,
    ol {
        padding: 0 1rem;
        margin: 1.25rem 1rem 1.25rem 0.4rem;

        li p {
            margin-top: 0.25em;
            margin-bottom: 0.25em;
        }
    }

    /* Heading styles */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        line-height: 1.1;
        margin-top: 2.5rem;
        text-wrap: pretty;
    }

    h1,
    h2 {
        margin-top: 3.5rem;
        margin-bottom: 1.5rem;
    }

    h1 {
        font-size: 1.4rem;
    }

    h2 {
        font-size: 1.2rem;
    }

    h3 {
        font-size: 1.1rem;
    }

    h4,
    h5,
    h6 {
        font-size: 1rem;
    }

    /* Code and preformatted text styles */
    code {
        background-color: var(--purple-light);
        border-radius: 0.4rem;
        color: var(--black);
        font-size: 0.85rem;
        padding: 0.25em 0.3em;
    }

    pre {
        background: var(--black);
        border-radius: 0.5rem;
        color: var(--white);
        font-family: 'JetBrainsMono', monospace;
        margin: 1.5rem 0;
        padding: 0.75rem 1rem;

        code {
            background: none;
            color: inherit;
            font-size: 0.8rem;
            padding: 0;
        }
    }

    blockquote {
        border-left: 3px solid var(--gray-3);
        margin: 1.5rem 0;
        padding-left: 1rem;
    }

    hr {
        border: none;
        border-top: 1px solid var(--gray-2);
        margin: 2rem 0;
    }

    /* Character count */
    .character-count {
        align-items: center;
        color: var(--gray-5);
        display: flex;
        font-size: 0.75rem;
        gap: .5rem;
        margin: 1.5rem;

        svg {
            color: var(--purple);
        }

        &--warning,
        &--warning svg {
            color: var(--red);
        }
    }
}
</style>